import { useState } from "react";
import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Check, Sparkles, Zap, Crown, Calculator, ArrowRight, HelpCircle, CreditCard, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import { useAuth } from "@/hooks/useAuth";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";

const FCFA_TO_USD = 0.0016;

const formatUSD = (fcfa: number) => {
  const usd = fcfa * FCFA_TO_USD;
  return `~$${usd.toFixed(0)}`;
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('fr-FR').format(price);
};

interface Plan {
  id: string;
  name: string;
  price: number;
  originalPrice: number | null;
  period: string;
  description: string;
  credits: string;
  creditsValue: number;
  maxReferrals: number;
  features: string[];
  cta: string;
  popular: boolean;
  icon: typeof Zap;
  gradient?: boolean;
}

const getPlans = (isAnnual: boolean): Plan[] => [
  {
    id: "free",
    name: "Free",
    price: 0,
    originalPrice: null,
    period: isAnnual ? "/an" : "/mois",
    description: "Testez Floop sans risque",
    credits: "10 crédits/mois",
    creditsValue: 10,
    maxReferrals: 10,
    features: [
      "10 générations HD/mois",
      "Tous les styles disponibles",
      "Export PNG & JPG",
      "Parrainez 10 amis (+2 crédits/ami)",
    ],
    cta: "Démarrer gratuitement",
    popular: false,
    icon: Zap,
  },
  {
    id: "starter",
    name: "Starter",
    price: isAnnual ? 58800 : 5900,
    originalPrice: isAnnual ? 70800 : null,
    period: isAnnual ? "/an" : "/mois",
    description: "Idéal pour les créateurs solo",
    credits: isAnnual ? "600 crédits/an (50/mois)" : "50 crédits/mois",
    creditsValue: isAnnual ? 600 : 50,
    maxReferrals: 50,
    features: [
      "50 crédits/mois",
      "Floop Pro débloqué",
      "Ad Studio inclus",
      "Support WhatsApp",
      "Parrainez 50 amis",
    ],
    cta: "Choisir Starter",
    popular: false,
    icon: Sparkles,
  },
  {
    id: "pro",
    name: "Pro",
    price: isAnnual ? 148800 : 14900,
    originalPrice: isAnnual ? 178800 : null,
    period: isAnnual ? "/an" : "/mois",
    description: "Pour les pros exigeants",
    credits: isAnnual ? "3 000 crédits/an (250/mois)" : "250 crédits/mois",
    creditsValue: isAnnual ? 3000 : 250,
    maxReferrals: 200,
    features: [
      "250 crédits/mois",
      "Qualité 4K native",
      "Ad Studio illimité",
      "Accès API complet",
      "Support dédié 7j/7",
    ],
    cta: "Passer Pro",
    popular: true,
    icon: Crown,
    gradient: true,
  },
  {
    id: "studio",
    name: "Studio Max",
    price: isAnnual ? 478800 : 47900,
    originalPrice: isAnnual ? 574800 : null,
    period: isAnnual ? "/an" : "/mois",
    description: "Solution équipe & agence",
    credits: isAnnual ? "6 000 crédits/an (500/mois)" : "500 crédits/mois",
    creditsValue: isAnnual ? 6000 : 500,
    maxReferrals: -1,
    features: [
      "500 crédits/mois",
      "Multi-utilisateurs",
      "API prioritaire",
      "Account manager dédié",
      "SLA 99.9% garanti",
    ],
    cta: "Contacter l'équipe",
    popular: false,
    icon: Crown,
  },
];

type ModelType = "basic" | "pro";
type FormatType = "standard" | "hd" | "4k";

const creditCosts: Record<ModelType, Record<FormatType, number>> = {
  basic: { standard: 1, hd: 1, "4k": 3 },
  pro: { standard: 5, hd: 5, "4k": 10 },
};

const CreditCalculator = () => {
  const [model, setModel] = useState<ModelType>("basic");
  const [format, setFormat] = useState<FormatType>("hd");
  const [quantity, setQuantity] = useState(20);
  const monthlyPlans = getPlans(false);

  const creditsPerImage = creditCosts[model][format];
  const totalCredits = creditsPerImage * quantity;

  const recommendedPlan = monthlyPlans.find(
    (plan) => plan.creditsValue >= totalCredits
  ) || monthlyPlans[monthlyPlans.length - 1];

  return (
    <div className="bg-card border border-border rounded-3xl p-6 md:p-8 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-gradient-floop flex items-center justify-center">
          <Calculator className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-foreground">Calculateur de crédits</h3>
          <p className="text-sm text-muted-foreground">Estimez vos besoins mensuels</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Model Selection */}
        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">Modèle</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setModel("basic")}
              className={cn(
                "p-4 rounded-2xl border-2 transition-all text-left",
                model === "basic"
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-primary" />
                <span className="font-semibold text-foreground">Floop Basic</span>
              </div>
              <p className="text-xs text-muted-foreground">Rapide et efficace</p>
            </button>
            <button
              onClick={() => setModel("pro")}
              className={cn(
                "p-4 rounded-2xl border-2 transition-all text-left",
                model === "pro"
                  ? "border-accent bg-accent/10"
                  : "border-border hover:border-accent/50"
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-accent" />
                <span className="font-semibold text-foreground">Floop Pro</span>
              </div>
              <p className="text-xs text-muted-foreground">Qualité studio</p>
            </button>
          </div>
        </div>

        {/* Format Selection */}
        <div>
          <label className="text-sm font-medium text-foreground mb-3 block">Format</label>
          <div className="grid grid-cols-3 gap-2">
            {(["standard", "hd", "4k"] as FormatType[]).map((f) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                className={cn(
                  "p-3 rounded-xl border-2 transition-all text-center",
                  format === f
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50"
                )}
              >
                <span className="text-sm font-medium text-foreground uppercase">{f}</span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {creditCosts[model][f]} crédit{creditCosts[model][f] > 1 ? "s" : ""}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Quantity Slider */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-medium text-foreground">Images par mois</label>
            <span className="text-lg font-bold text-primary">{quantity}</span>
          </div>
          <Slider
            value={[quantity]}
            onValueChange={(value) => setQuantity(value[0])}
            min={1}
            max={200}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>1</span>
            <span>50</span>
            <span>100</span>
            <span>150</span>
            <span>200</span>
          </div>
        </div>

        {/* Results */}
        <div className="bg-secondary/50 rounded-2xl p-5">
          <div className="flex justify-between items-center mb-3">
            <span className="text-muted-foreground">Crédits par image</span>
            <span className="font-semibold text-foreground">{creditsPerImage}</span>
          </div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-muted-foreground">Nombre d'images</span>
            <span className="font-semibold text-foreground">×{quantity}</span>
          </div>
          <div className="border-t border-border pt-3">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-foreground">Total</span>
              <span className="text-2xl font-bold bg-gradient-floop bg-clip-text text-transparent">
                {totalCredits} crédits
              </span>
            </div>
          </div>
        </div>

        {/* Recommendation */}
        <div className="bg-primary/10 border border-primary/30 rounded-2xl p-4">
          <p className="text-sm text-muted-foreground mb-2">Plan recommandé</p>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xl font-bold text-foreground">{recommendedPlan.name}</span>
              <span className="text-muted-foreground ml-2 text-sm">{recommendedPlan.credits}</span>
            </div>
            <div className="text-right">
              <span className="text-xl font-bold text-primary">{formatPrice(recommendedPlan.price)} FCFA</span>
              {recommendedPlan.price > 0 && (
                <span className="block text-xs text-muted-foreground">{formatUSD(recommendedPlan.price)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const faqs = [
  {
    question: "Comment fonctionne le système de crédits Floop ?",
    answer: "Chaque image générée consomme des crédits selon le modèle choisi : Floop Basic = 1 crédit (3 en 4K), Floop Pro = 5 crédits (10 en 4K), Ad Studio = 10 crédits par plateforme. Vos crédits non utilisés sont automatiquement reportés au mois suivant (plans payants)."
  },
  {
    question: "Qu'est-ce que Ad Creative Studio ?",
    answer: "Ad Studio est notre outil de création de publicités professionnelles pour les réseaux sociaux (Instagram, Facebook, TikTok, Twitter, Pinterest, LinkedIn). Uploadez votre produit, choisissez vos plateformes, et notre IA génère des créatives optimisées. 10 crédits par plateforme sélectionnée."
  },
  {
    question: "Puis-je changer ou annuler mon plan ?",
    answer: "Absolument ! Aucun engagement, aucune pénalité. Upgradez pour plus de crédits ou downgradez selon vos besoins. Le changement est instantané et la facturation est ajustée au prorata. Vous gardez le contrôle total."
  },
  {
    question: "Quels moyens de paiement acceptez-vous ?",
    answer: "Nous acceptons Mobile Money (MTN MoMo, Orange Money), PayPal, et les cartes bancaires internationales (Visa, Mastercard). Pour les entreprises, nous proposons aussi le virement bancaire et la facturation personnalisée."
  },
  {
    question: "Y a-t-il une période d'essai ou un engagement ?",
    answer: "Le plan Free est 100% gratuit, sans limite de temps. 10 crédits offerts chaque mois pour tester Floop. Aucune carte bancaire requise. Pour les plans payants : zéro engagement, annulation en 1 clic."
  },
  {
    question: "Mes crédits non utilisés sont-ils perdus ?",
    answer: "Jamais perdus ! Pour les plans payants, vos crédits sont cumulables et reportés au mois suivant (jusqu'à 2x votre allocation). Pour le plan Free, les crédits se renouvellent chaque mois à 10."
  },
  {
    question: "Comment fonctionne le parrainage ?",
    answer: "Invitez vos amis avec votre lien unique et gagnez 2 crédits bonus par inscription réussie. Free = 10 parrainages max, Starter = 50, Pro = 200, Studio Max = illimité. C'est notre façon de dire merci à notre communauté !"
  },
  {
    question: "Quelle est la différence entre Floop Basic et Floop Pro ?",
    answer: "Floop Basic est rapide et efficace pour les créations quotidiennes. Floop Pro offre une qualité studio professionnelle avec des retouches avancées et des détails ultra-précis. Parfait pour les contenus premium et commerciaux."
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isAnnual, setIsAnnual] = useState(false);
  const plans = getPlans(isAnnual);

  const handleSubscribe = (plan: Plan) => {
    if (plan.id === "free") {
      navigate("/auth");
      return;
    }

    if (plan.id === "studio") {
      window.open("https://wa.me/237697747365?text=Bonjour, je suis intéressé par le plan Studio Max", "_blank");
      return;
    }

    // Pass plan data to payment page
    const paymentData = {
      planId: plan.id,
      planName: plan.name,
      price: plan.price,
      credits: plan.creditsValue,
      period: isAnnual ? "annual" : "monthly",
    };

    if (isAuthenticated) {
      navigate("/payment", { state: { subscription: paymentData } });
    } else {
      navigate("/auth", { state: { from: "/payment", subscription: paymentData } });
    }
  };

  return (
    <div className="min-h-screen bg-background page-transition">
      <SEO 
        title="Tarifs Floop 2024 : Plans Flexibles dès 0 FCFA | Génération d'Images IA"
        description="Créez des visuels IA professionnels avec Floop. Plan gratuit (10 crédits/mois), Starter (50 crédits), Pro (250 crédits). Mobile Money accepté. Sans engagement."
        keywords="tarifs floop, prix génération image IA, abonnement créateur, crédits floop, plan gratuit IA, mobile money cameroun"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-24 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-50" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 blur-3xl rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 blur-3xl rounded-full" />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-4 animate-fade-up">
              <CreditCard className="w-4 h-4" />
              Tarification transparente
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display animate-fade-up stagger-1">
              Des tarifs{" "}
              <span className="bg-gradient-floop bg-clip-text text-transparent">transparents et flexibles</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground animate-fade-up stagger-2">
              Commencez gratuitement. Évoluez à votre rythme. Annulez quand vous voulez.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mt-10 animate-fade-up stagger-3">
              <span className={cn(
                "text-sm font-medium transition-colors",
                !isAnnual ? "text-foreground" : "text-muted-foreground"
              )}>
                Mensuel
              </span>
              <Switch
                checked={isAnnual}
                onCheckedChange={setIsAnnual}
              />
              <span className={cn(
                "text-sm font-medium transition-colors",
                isAnnual ? "text-foreground" : "text-muted-foreground"
              )}>
                Annuel
              </span>
              {isAnnual && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-success/20 text-success text-xs font-semibold animate-scale-in">
                  -17% économisé
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="py-8 md:py-12 relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={plan.id}
                className={cn(
                  "relative bg-card border rounded-2xl md:rounded-3xl p-4 md:p-6 transition-all duration-300 hover:-translate-y-2 animate-fade-up",
                  plan.popular
                    ? "border-primary shadow-xl shadow-primary/20 lg:scale-105 z-10"
                    : "border-border hover:border-primary/50",
                  `stagger-${index + 1}`
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-floop text-white text-xs font-semibold px-3 md:px-4 py-1 md:py-1.5 rounded-full flex items-center gap-1 shadow-lg whitespace-nowrap">
                      <Sparkles className="w-3 h-3" />
                      Populaire
                    </span>
                  </div>
                )}

                <div className="text-center mb-4 md:mb-6 pt-2">
                  <div className={cn(
                    "w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4",
                    plan.gradient ? "bg-gradient-floop" : "bg-primary/20"
                  )}>
                    <plan.icon className={cn(
                      "w-5 h-5 md:w-7 md:h-7",
                      plan.gradient ? "text-white" : "text-primary"
                    )} />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground">{plan.name}</h3>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="text-center mb-4 md:mb-6">
                  {plan.originalPrice && (
                    <div className="text-xs md:text-sm text-muted-foreground line-through mb-1">
                      {formatPrice(plan.originalPrice)} FCFA
                    </div>
                  )}
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl md:text-4xl font-bold text-foreground">
                      {plan.price === 0 ? "Gratuit" : formatPrice(plan.price)}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-muted-foreground text-xs md:text-sm">FCFA{plan.period}</span>
                    )}
                  </div>
                  {plan.price > 0 && (
                    <div className="text-xs text-accent mt-1">{formatUSD(plan.price)} USD</div>
                  )}
                  <div className="text-xs md:text-sm text-primary font-medium mt-2 md:mt-3">{plan.credits}</div>
                </div>

                {/* Features */}
                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs md:text-sm">
                      <Check className="w-3 h-3 md:w-4 md:h-4 text-success flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => handleSubscribe(plan)}
                  className={cn(
                    "w-full rounded-full group text-sm md:text-base py-2 md:py-2.5",
                    plan.popular
                      ? "bg-gradient-floop hover:opacity-90 text-white"
                      : "bg-secondary hover:bg-secondary/80 text-foreground"
                  )}
                >
                  {plan.cta}
                  <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-8 mt-16 text-sm text-muted-foreground animate-fade-up">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-success" />
              <span>Paiement sécurisé</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-success" />
              <span>Annulation à tout moment</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-success" />
              <span>Mobile Money accepté</span>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Calculator */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display animate-fade-up">
              Combien de crédits avez-vous besoin ?
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto animate-fade-up stagger-1">
              Simulez votre consommation mensuelle et trouvez le plan idéal en 30 secondes.
            </p>
          </div>
          <CreditCalculator />
        </div>
      </section>

      {/* Floop Models */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display animate-fade-up">
              Deux moteurs IA, un seul objectif : des visuels parfaits
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto animate-fade-up stagger-1">
              Choisissez la puissance adaptée à vos projets. Du brainstorm rapide au rendu professionnel.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Floop Basic */}
            <div className="bg-card border border-border rounded-3xl p-8 hover:-translate-y-2 transition-all animate-fade-up">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                  <Zap className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Floop Basic</h3>
                  <p className="text-muted-foreground">Rapide & polyvalent</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-6">
                Parfait pour vos idées créatives quotidiennes. Générez des visuels en quelques secondes pour vos réseaux sociaux, moodboards et prototypes.
              </p>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">HD (1024px)</span>
                  <span className="font-semibold text-foreground">1 crédit</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-muted-foreground">4K (2048px)</span>
                  <span className="font-semibold text-foreground">3 crédits</span>
                </div>
              </div>
            </div>

            {/* Floop Pro */}
            <div className="bg-card border border-accent/30 rounded-3xl p-8 relative overflow-hidden hover:-translate-y-2 transition-all animate-fade-up stagger-1">
              <div className="absolute top-4 right-4">
                <span className="bg-gradient-floop text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Qualité Studio
                </span>
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-floop flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Floop Pro</h3>
                  <p className="text-muted-foreground">Précision maximale</p>
                </div>
              </div>
              <p className="text-muted-foreground mb-6">
                Notre moteur premium pour les visuels commerciaux. Retouches professionnelles, détails ultra-fins et rendus dignes des plus grands studios.
              </p>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-border">
                  <span className="text-muted-foreground">HD (1024px)</span>
                  <span className="font-semibold text-foreground">5 crédits</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-muted-foreground">4K (2048px)</span>
                  <span className="font-semibold text-foreground">10 crédits</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 text-primary mb-4">
              <HelpCircle className="w-5 h-5" />
              <span className="text-sm font-medium">FAQ</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display animate-fade-up">
              Vos questions, nos réponses
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto animate-fade-up stagger-1">
              Tout ce qu'il faut savoir avant de commencer avec Floop.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-2xl px-6 data-[state=open]:border-primary/50"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-floop opacity-10" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display animate-fade-up">
            Lancez-vous, c'est gratuit
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto animate-fade-up stagger-1">
            Créez votre première image en moins de 2 minutes. 10 crédits offerts, aucune carte bancaire requise.
          </p>
          <Button
            onClick={() => handleSubscribe(plans[0])}
            size="lg"
            className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-10 py-6 text-lg font-semibold group animate-fade-up stagger-2"
          >
            Créer ma première image
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Pricing;
